import React, { useMemo, useState } from 'react';
import { X, Inbox as InboxIcon, ArrowRight } from 'lucide-react';
import { useCapacityBlockStore } from '../stores/capacityBlockStore';
import { useInboxStore } from '../stores/inboxStore';
import { useQuestStore } from '../stores/questStore';
import { useAbilitiesStore } from '../stores/abilitiesStore';
import { useCurrentView } from '../hooks/useCurrentView';
import { useCapacityStatus } from '../hooks/useCapacityStatus';
import { CAPACITY_ESCAPES, type CapacityEscape } from '../utils/capacityEscapes';
import { INBOX_DESTINATION_FOR } from '../utils/capacityGuard';
import { ACTION_TYPE_BY_ID } from '../constants/achievementBadges';
import { CATEGORIES, domainDisplayName } from '../constants/categories';
import type { ActiveItem } from '../utils/capacityCore';

/**
 * Shown when a create path is stopped by the domain's capacity.
 *
 * ⚠️⚠️ THE POINT OF THIS MODAL IS THAT THE BLOCK HAS A WAY OUT. A cap with no
 * reachable remedy is the bug this codebase spent a session removing, so every
 * option below is checked against the number that actually blocked:
 *  · Complete / Archive appear only for types where the action provably reduces
 *    the active holding — see the header of `utils/capacityEscapes.ts`.
 *  · The list is `status.items`, the very records the count came from, so
 *    clearing one always moves the number. It is on screen live, and the header
 *    reading updates as the user works.
 *  · The Inbox is offered for EVERY type, including the three (abilities,
 *    skills, routines-without-archive) where nothing else applies.
 *
 * ⚠️ NOTHING HERE DELETES. Users already over their allowance keep every record
 * they hold; the modal only ever offers reversible moves (archive restores,
 * completion un-toggles) and capture.
 */
export function CapacityBlockModal() {
  const block = useCapacityBlockStore(s => s.block);
  const close = useCapacityBlockStore(s => s.closeCapacityBlock);
  const status = useCapacityStatus();

  if (!block) return null;
  return <CapacityBlockDialog key={`${block.type}:${block.domainId}:${block.parentId ?? ''}`}
    block={block} close={close} read={status} />;
}

function CapacityBlockDialog({ block, close, read }: {
  block: NonNullable<ReturnType<typeof useCapacityBlockStore.getState>['block']>;
  close: () => void;
  read: ReturnType<typeof useCapacityStatus>;
}) {
  const capture = useInboxStore(s => s.capture);
  const categorize = useInboxStore(s => s.categorize);
  const classify = useInboxStore(s => s.classify);
  const setTarget = useInboxStore(s => s.setTarget);
  const quests = useQuestStore(s => s.quests);
  const abilities = useAbilitiesStore(s => s.abilities);
  const setCurrentView = useCurrentView(s => s.setCurrentView);

  const [captureTitle, setCaptureTitle] = useState(block.draftTitle ?? '');
  const [capturedId, setCapturedId] = useState<string | null>(null);

  const def = ACTION_TYPE_BY_ID[block.type];
  const escapes = CAPACITY_ESCAPES[block.type];
  const status = read(block.type, block.domainId, block.parentId);
  const noun = def.label.toLowerCase();
  /** A multi-create (an AI plan applying at once) reads differently from one create. */
  const batch = (block.adding ?? 1) > 1;
  const domainName = domainDisplayName(
    CATEGORIES.find(c => c.id === block.domainId)?.name ?? block.domainId);

  // ⚠️ The SUBJECT changes with `capacityPer`, exactly as it does on the
  // indicator: a per-parent allowance is held by the quest or ability, not by
  // the domain, so "this domain is full of goals" would name the wrong owner.
  const parentLabel = useMemo(() => {
    if (!block.parentId) return null;
    if (block.type === 'goals') return quests.find(q => q.id === block.parentId)?.title ?? null;
    if (block.type === 'skills') return abilities.find(a => a.id === block.parentId)?.name ?? null;
    return null;
  }, [block.parentId, block.type, quests, abilities]);

  const holder = status?.per && parentLabel ? `“${parentLabel}”` : domainName;

  /**
   * ⚠️ NO BYPASS — this is the ordinary capture path, three real store actions,
   * and the item lands at the `capture` stage like any other. It is prefilled,
   * not fast-tracked: the user still walks Clarify → Place → Prioritize →
   * Classify → Route before it becomes anything. Writing a half-formed record
   * straight into a later stage is exactly what the funnel exists to prevent.
   */
  const sendToInbox = () => {
    const title = captureTitle.trim();
    if (!title) return;
    const id = capture(title, block.draftDescription);
    categorize(id, block.domainId);
    classify(id, INBOX_DESTINATION_FOR[block.type]);
    // ⭐ Goals and Skills are nested-only (`ATTACH_SPEC.required`), and we know
    // which quest or ability the user was standing in — so the target that Route
    // would otherwise block on is filled in too.
    if (block.parentId && parentLabel) {
      if (block.type === 'goals') setTarget(id, { type: 'quest', id: block.parentId, label: parentLabel });
      if (block.type === 'skills') setTarget(id, { type: 'ability', id: block.parentId, label: parentLabel });
    }
    setCapturedId(id);
  };

  const openInbox = () => { close(); setCurrentView('inbox'); };

  const hasRemedy = Boolean(escapes.complete || escapes.archive);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-card shadow-lg">
        <div className="flex items-start justify-between border-b border-border p-6">
          <div>
            {/* ⚠️⚠️ A BATCH CAN BE REFUSED WITHOUT THE DOMAIN BEING FULL — eight
                requested against two-of-five used. "…is at capacity" above a
                "2 of 5" reading would contradict itself on screen, so a batch
                gets its own headline naming what it tried to add. */}
            <h2 className="text-xl font-semibold text-foreground">
              {batch
                ? `${holder} has room for ${Math.max(0, (status?.max ?? 0) - (status?.used ?? 0))} more ${noun}, not ${block.adding}`
                : `${holder} is at capacity for ${def.label}`}
            </h2>
            {/* ⚠️ State the number that blocked. "You've reached your limit" with
                no figure is unanswerable — the user cannot tell how far over
                they are or what would be enough. */}
            <p className="mt-1 text-sm text-muted-foreground">
              {status
                ? `${status.used} of ${status.max} active ${noun}${status.per ? ` per ${status.per}` : ''}. `
                : ''}
              {batch ? 'Nothing was created and nothing was charged. ' : ''}
              The allowance grows as you complete {noun} in {domainName}.
            </p>
          </div>
          <button
            onClick={close}
            aria-label="Close"
            className="rounded-full p-2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          {escapes.complete && status && (
            <EscapeSection
              heading={`Complete ${noun.replace(/s$/, '')} to free a slot`}
              escape={escapes.complete}
              items={status.items}
              domainId={block.domainId}
            />
          )}

          {escapes.archive && status && (
            <EscapeSection
              heading={`Or archive something`}
              escape={escapes.archive}
              items={status.items}
              domainId={block.domainId}
            />
          )}

          {/* ⚠️ Say so PLAINLY when neither remedy exists. Abilities and Skills
              have no completed and no archived state, so pretending otherwise
              with a greyed-out button would just look broken. */}
          {!hasRemedy && (
            <p className="rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              {def.label} have no completed or archived state, so the only ways to free a
              slot are to remove one you no longer want — from its own page — or to raise
              the allowance by {def.verb === 'levels gained in' ? 'levelling up an ability' : `finishing ${noun}`} in {domainName}.
            </p>
          )}

          <div className="rounded-md border border-border p-4">
            <div className="flex items-center gap-2">
              <InboxIcon className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold text-foreground">Put it in the Inbox instead</h3>
            </div>
            {capturedId ? (
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Captured in your Inbox, filed under {domainName} as a {noun.replace(/s$/, '')}.
                  Nothing was created yet — decide what it becomes when there is room.
                </p>
                <button
                  onClick={openInbox}
                  className="flex shrink-0 items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
                >
                  Open Inbox <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <p className="mt-1 text-sm text-muted-foreground">
                  Keeps the thought without committing to it. The domain and the action
                  type come with it, so it is ready to promote when a slot opens.
                </p>
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={captureTitle}
                    onChange={e => setCaptureTitle(e.target.value)}
                    placeholder={`What were you adding?`}
                    className="min-w-0 flex-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-input focus:ring-2 focus:ring-ring"
                  />
                  <button
                    onClick={sendToInbox}
                    disabled={!captureTitle.trim()}
                    className="shrink-0 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                  >
                    Capture
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end border-t border-border p-4">
          <button
            onClick={close}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * One remedy and the records it applies to.
 *
 * ⭐ The list shrinks as the user works, because it is `status.items` read live
 * — which is also the proof that the remedy is real. If pressing Complete left
 * the row in place, the escape would not be freeing capacity.
 */
function EscapeSection({ heading, escape, items, domainId }: {
  heading: string;
  escape: CapacityEscape;
  items: ActiveItem[];
  domainId: string;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground">{heading}</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">{escape.hint}</p>
      <ul className="mt-3 space-y-1">
        {items.map(item => {
          const blockedReason = escape.blockedReason?.(item) ?? null;
          return (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{item.label}</span>
              {blockedReason ? (
                <span className="shrink-0 text-xs text-muted-foreground">{blockedReason}</span>
              ) : (
                <button
                  onClick={() => escape.run(item, domainId)}
                  className="shrink-0 rounded-md border border-input px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                >
                  {escape.label}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
